import { GitBranch, CheckCircle2, Clock, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Rolled-up state of the order's OTHER live fulfilment-order parts.
 *  - `rest_shipped`    every other part has already gone out.
 *  - `in_production`   at least one other part is still on a floor somewhere.
 * Omit it on surfaces that only know "this row is a split half" (the pre-batch
 * Order Queue, which has no sibling roll-up) — the badge then says nothing it
 * cannot back up.
 */
export type SplitBadgeState = 'rest_shipped' | 'in_production';

const STATE_DESCRIPTORS: Record<SplitBadgeState, { labelKey: string; icon: LucideIcon }> = {
  rest_shipped: { labelKey: 'issues.splitLocation.restShipped', icon: CheckCircle2 },
  in_production: { labelKey: 'issues.splitLocation.restInProduction', icon: Clock },
};

interface SplitBadgeProps {
  /**
   * Total live parts of the order (this row + its siblings). ≥2 renders the
   * part count; anything else falls back to the bare "Split" pill, so a stale
   * payload degrades to the weaker true statement rather than a wrong number.
   */
  count?: number;
  /** See {@link SplitBadgeState}. Drives the trailing glyph + words, not colour. */
  state?: SplitBadgeState;
  /**
   * This part's own location, for the pre-batch Order Queue where "which half
   * am I looking at" is the question. Redacted/unknown → the bare pill.
   */
  location?: string | null;
  className?: string;
}

/**
 * BLU-563 / BLU-758: the one split-order pill, shared by the pre-batch Order
 * Queue (`OrderSelectionList`) and the Production Tracker's Orders + Queue
 * tables.
 *
 * WHY ONE COMPONENT: a split order shows up on three surfaces an operator moves
 * between in a single shift. Before this, only the Order Queue had a pill, and
 * the tracker — the surface people actually watch — showed a short item count
 * with no explanation, which reads as a picking error. Splitting the vocabulary
 * across surfaces would be worse than having none.
 *
 * DISCIPLINE:
 *  - `GitBranch` glyph always: the constant that says "this is one half of a
 *    bigger order".
 *  - ALWAYS `muted`, never a semantic colour. Every other pill on these rows
 *    (status, Redo, Hybrid, TEST) colours THIS row's state, so an emerald pill
 *    on an IN_PROGRESS row reads "this shipped" at a glance from across the
 *    room — exactly the misread PRODUCTION_DESIGN.md §4.1 exists to prevent.
 *    The sibling roll-up is about OTHER rows, so it stays achromatic and lets
 *    its glyph + words carry the state. Semantic colour for the siblings lives
 *    where it is unambiguous: the per-sibling status pills in the expanded row.
 *  - Words + glyph, never colour alone (§13) — nothing here depends on hue.
 *  - Non-interactive `<span>` (Badge renders a span) and NO tooltip. The row
 *    click owns expansion, and §10 bans tooltips carrying meaning — these are
 *    tablet surfaces where hover does not exist. The detail lives in the
 *    expanded row (`ExpandedOrderContent`'s sibling summary), which is visible
 *    without hover.
 */
export function SplitBadge({ count, state, location, className }: SplitBadgeProps) {
  const { t } = useTranslation('production');

  const label = location
    ? t('issues.splitLocation.queueBadge', { location })
    : count && count >= 2
      ? t('issues.splitLocation.queueBadgeParts', { count })
      : t('issues.splitLocation.queueBadgeShort');

  const descriptor = state ? STATE_DESCRIPTORS[state] : null;

  return (
    <Badge
      variant="muted"
      size="sm"
      // `whitespace-nowrap` ONLY on the short forms ("Split", "Split · 3 parts",
      // "Split · Ningbo"). The two-clause form is ~200px wide, and nowrap on it
      // sets that as a min-content floor for the whole Order column — one split
      // row would then squeeze every other column on a laptop. Let it wrap; the
      // cluster around it already wraps.
      className={cn('shrink-0', !descriptor && 'whitespace-nowrap', className)}
    >
      <GitBranch className="h-3 w-3" aria-hidden="true" />
      {label}
      {descriptor && (
        <>
          <span className="opacity-50" aria-hidden="true">·</span>
          <descriptor.icon className="h-3 w-3" aria-hidden="true" />
          {t(descriptor.labelKey)}
        </>
      )}
    </Badge>
  );
}
